import { Injectable, Injector, inject } from "@angular/core";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { Fce } from "./fce";
import { SharedService } from "../_shared/shared.service";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { DbConstants } from "../_consts/db-constants";
import { SignalBatteryDataStore } from "src/app/_stores/BatteryDataStore";
import { SignalBatteryDetailSnapshotStore } from "src/app/_stores/BatteryDetailSnapshotStore";
import { SignalEmsPortalDataStore } from "src/app/_stores/EmsPortalDataStore";
import { SignalFreestandingBatteryDataStore } from "src/app/_stores/FreestandingBatteryDataStore";
import { SignalLocationDataStore } from "src/app/_stores/LocationDataStore";
import { SignalSkidDataStore } from "src/app/_stores/SkidDataStore";
import { LocationStore } from "src/app/_stores/LocationStore";
import { SignalBatteryRevisionsStore } from "src/app/_stores/BatteryRevisionsStore";
import {
  SignalBatteryVersionsStore,
  SignalBatteryVersionTypesStore,
} from "src/app/_stores/BatteryVersionsStore";
import { SignalTwinChargerDataStore } from "src/app/_stores/TwinChargerDataStore";
import { SignalTwinChargerDetailSnapshotStore } from "src/app/_stores/TwinChargerDetailSnapshotStore";
import { LoggedUserService } from "src/app/_services/logged-user.service";

/** Klíč end-session URL Keycloaku — header logout podle něj odhlásí i SSO session. */
export const SSO_LOGOUT_KEY = "sso_logout";

@Injectable({ providedIn: "root" })
export class AuthService {
  private injector = inject(Injector);
  private storesInitialized = false;

  private tokenRefresh;
  private msgsRefresh;
  private min1 = 60 * 1000; //1min
  private min5 = 5 * this.min1; //5min
  private refreshTime = 30 * this.min1; //30min
  private minRefreshTime = 10 * 1000; //10sec
  public logging;
  private fcmListener;

  constructor(
    private http: HttpClient,
    protected shared: SharedService,
    private router: Router,
    private loggedUser: LoggedUserService,
  ) {
    try {
      const logged = JSON.parse(shared.storageGet("login"));
      const diff = this.tokenValidDiff(logged);

      if (logged && diff > this.minRefreshTime) {
        this.shared.login = logged;
        this.controlUser(false);
      }
    } catch (error) {
      this.shared.login = null;
    }
  }

  tokenValidDiff(loginObj) {
    const time = new Date().getTime() * 0.001;
    return Math.round(loginObj.expires_at - time) * 1000;
  }

  /** ssoToken = Keycloak access token, backend ho vymění za EML JWT (ssoLogin). */
  async loginUser(params, fastLogin = false, ssoToken?: string) {
    if (this.logging) return;
    this.logging = true;
    // heslový login nesmí zdědit end-session URL z předchozí SSO session
    if (!ssoToken) this.shared.storageDelete(SSO_LOGOUT_KEY);
    params.id_browser = this.shared.storageGet("id_browser") || null;
    const endpoint = ssoToken ? "ssoLogin" : fastLogin ? "fastLogin" : "login";
    const response: any = await this.http
      .post(`${environment.apiUrl}${endpoint}`, params, ssoToken ? { headers: { Authorization: `Bearer ${ssoToken}` } } : {})
      .toPromise()
      .catch((e) => this.shared.logError(e));
    if (!response) {
      this.logging = false;
      return;
    }

    this.processLoginAndEtc(
      {
        ...response.login,
        browser_sett: response.browser_sett,
      },
      response.rights,
      response.messages,
      response.constants,
      response.groups,
      response.roles,
      response.modules,
      true,
      response.leader_team_ids,
      response.general_ntfy_url,
      response.general_ntfy_web_url,
    );
    this.logging = false;

    if (response.remember_hash) {
      try {
        let remembers: any = this.shared.storageGet("remembers");
        remembers = remembers ? JSON.parse(remembers) : {};
        remembers[this.shared.login.id_user] = {
          fullname: this.shared.login.fullname,
          id_user: this.shared.login.id_user,
          hash: response.remember_hash,
        };
        this.shared.storageSet("remembers", JSON.stringify(remembers));
      } catch (error) {
        this.shared.logError(error);
      }
    }

    this.setTokenRefresh();
    if (!fastLogin) this.requestFcmToken();
  }

  async controlUser(refreshToken = true, showLoad = true) {
    if (!this.shared.login || !this.shared.login.id_user) {
      this.shared.rightsAll = null;
      return;
    }
    this.shared.refreshingToken = true;
    const response: any = await this.http
      .post(`${environment.apiUrl}refresh`, {
        id_user: this.shared.login.id_user,
        setting: !this.shared.rightsAll,
        refresh: refreshToken,
        hideLoader: !showLoad,
        id_browser: this.shared.storageGet("id_browser") || null,
      })
      .toPromise()
      .catch((e) => this.shared.logError(e));
    if (!response) {
      this.shared.refreshingToken = false;
      return;
    }

    this.processLoginAndEtc(
      {
        ...this.shared.login,
        ...response.token,
        fcm_token: response.token,
        browser_sett: response.browser_sett,
      },
      response.rights,
      response.messages,
      response.constants,
      response.groups,
      response.roles,
      response.modules,
      false,
      response.leader_team_ids,
      response.general_ntfy_url,
      response.general_ntfy_web_url,
    );
    this.shared.refreshingToken = false;

    this.setTokenRefresh();
  }

  processLoginAndEtc(
    login,
    rights,
    messages,
    constants,
    groups,
    roles = null,
    modules = null,
    controlLang = false,
    leaderTeamIds = null,
    generalNtfyUrl = undefined,
    generalNtfyWebUrl = undefined,
  ) {
    this.shared.storageSet("login", JSON.stringify(login));
    this.shared.login = login;
    this.shared.login.id_browser = this.shared.storageGet("id_browser") || "";
    // Skupiny chodí v odpovědi VEDLE rights (login.groups nikdy neexistoval —
    // groupsAll byl proto vždy prázdný a isAdmin/hasGroupByName visely jen na
    // rights fallbacku). Periodický refresh bez `setting` je neposílá →
    // stávající hodnotu nepřepisovat.
    if (groups) this.shared.groupsAll = groups;
    // Populate the module/role/group source-of-truth BEFORE flipping `rightsAll`.
    // `rightsAll = rights` runs its subscribers synchronously (BehaviorSubject),
    // and AccessGuard waits on the first non-null `rightsAll` to re-check access.
    // If modules were filled after, that re-check would still see empty modules
    // and wrongly reject the user — so `rightsAll` must be the LAST auth flag set.
    this.loggedUser.update({
      username: login.username,
      fullname: login.fullname,
      groups,
      roles,
      modules,
      leader_team_ids: leaderTeamIds,
    });
    if (rights) this.shared.rightsAll = rights;
    // Explicit undefined = this call never fetched settings (plain refresh) —
    // leave the existing value. Explicit null = settings were fetched and the
    // module isn't/no-longer granted.
    if (generalNtfyUrl !== undefined) this.shared.generalNtfyUrl = generalNtfyUrl;
    if (generalNtfyWebUrl !== undefined) this.shared.generalNtfyWebUrl = generalNtfyWebUrl;
    if (messages) this.shared.messages.next(messages);
    if (constants) {
      DbConstants.parse(constants);
    }
    console.log("LOGIN/REFRESH", login, rights, messages, constants);

    if (
      controlLang &&
      !Fce.empty(login.lang) &&
      login.lang != this.shared.transService.currentLang
    ) {
      this.shared.broadcast("CHANGE_LANG", login.lang);
    }

    //Refresh in iframe
    if (this.shared.loadedIframe && this.shared.loadedIframe.loaded) {
      this.shared.loadedIframe.sendLogin();
    }

    if (login.fcm_token) {
      this.listenFcm();
    }

    this.initStores();
  }

  private initStores(): void {
    if (this.storesInitialized) return;
    this.storesInitialized = true;

    // No auto-refresh (one-shot load on injection) - safe to init unconditionally.
    this.injector.get(LocationStore);
    this.injector.get(SignalBatteryRevisionsStore);

    // Auto-refreshing stores poll their endpoint every 30s for as long as the
    // store is alive, so each is only initialised once the user actually has
    // the module its endpoint is gated behind - otherwise it would just poll
    // an endpoint that always 403s.
    if (this.loggedUser.hasModule("Battery")) {
      this.injector.get(SignalBatteryDataStore);
      this.injector.get(SignalBatteryDetailSnapshotStore);
    }
    if (this.loggedUser.hasModule("BatteryVersion")) {
      this.injector.get(SignalBatteryVersionsStore);
      this.injector.get(SignalBatteryVersionTypesStore);
    }
    if (this.loggedUser.hasModule("Location")) {
      this.injector.get(SignalEmsPortalDataStore);
      this.injector.get(SignalFreestandingBatteryDataStore);
      this.injector.get(SignalLocationDataStore);
      this.injector.get(SignalSkidDataStore);
      this.injector.get(SignalTwinChargerDataStore);
      this.injector.get(SignalTwinChargerDetailSnapshotStore);
    }
  }
  requestFcmToken() {
    // const isSupported = () =>
    // 'Notification' in window &&
    // 'serviceWorker' in navigator &&
    // 'PushManager' in window

    // if(!isSupported()){
    //     this.shared.warning("FCM NOT SUPPORTED IN THIS BROWSER")
    //     return
    // }

    if (environment.firebase) {
      const messaging = getMessaging();
      console.log("bbbb", environment.firebase);
      getToken(messaging, { vapidKey: environment.firebase.vapidKey })
        .then((currentToken) => {
          if (currentToken) {
            this.listenFcm();
            this.setFcmBackend(currentToken);
          } else this.shared.warning("FCM TOKEN NOW AVAILABLE");
        })
        .catch((err) => {
          if (!(
            err?.includes &&
            err.includes("The notification permission was not granted")
          ))
            this.shared.warning("FCM TOKEN ERROR: " + err);
        });
    }
  }

  listenFcm() {
    if (this.fcmListener) this.fcmListener();
    console.log("LISTEN FCM");
    if (environment.firebase) {
      this.fcmListener = onMessage(getMessaging(), (payload) => {
        if (payload?.notification)
          this.shared.info(
            payload?.notification.title,
            payload?.notification.body,
            0,
          );
        else this.shared.warning("FCM MESSAGE", JSON.stringify(payload));
      });
    }
  }

  setFcmBackend(token) {
    this.shared.login.fcm_token = token;
    this.http
      .post(`${environment.apiUrl}${"setFcm"}`, { token })
      .toPromise()
      .catch((e) => this.shared.logError(e));
  }

  logoutUser(goToLogin: boolean) {
    this.shared.reset();
    this.loggedUser.clear();
    if (goToLogin) this.router.navigate(["/login"]);

    if (this.tokenRefresh) clearTimeout(this.tokenRefresh);
    if (this.msgsRefresh) clearTimeout(this.msgsRefresh);

    if (this.fcmListener) {
      this.fcmListener();
    }
  }

  setTokenRefresh() {
    if (this.tokenRefresh) {
      clearTimeout(this.tokenRefresh);
      this.tokenRefresh = null;
    }

    if (this.shared.isLoggedIn() && this.shared.login.expires_at) {
      const diff = this.tokenValidDiff(this.shared.login);
      const self = this;

      if (diff > this.minRefreshTime) {
        this.tokenRefresh = setTimeout(
          () => {
            console.log("REFRESH TOKEN");
            self.controlUser(true, false);
          },
          diff > this.refreshTime ? this.refreshTime : diff,
        );
      } else this.logoutUser(true);
    }

    this.setMsgsRefresh();
  }

  setMsgsRefresh() {
    if (this.msgsRefresh) {
      clearTimeout(this.msgsRefresh);
      this.msgsRefresh = null;
    }
    if (this.shared.isLoggedIn() && environment.headerMessages) {
      const self = this;
      this.msgsRefresh = setTimeout(async () => {
        if (this.shared.isLoggedIn() && !this.shared.refreshingToken) {
          const response: any = await this.http
            .post(`${environment.apiUrl}msg/getMessages`, { hideLoader: true })
            .toPromise()
            .catch((e) => this.shared.logError(e));
          if (response && response.messages)
            this.shared.messages.next(response.messages);
          self.setMsgsRefresh();
        }
      }, this.min5);
    }
  }
}
